import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Container } from '../pages/Home.styled';
import { N8N_WEBHOOK_URL } from '../config';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  z-index: 1000;
  padding: 20px;
  box-sizing: border-box;

  /* 애니메이션 */
  animation: fadeIn 0.3s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const Modal = styled.div`
  width: min(760px, 92vw);
  max-height: calc(100vh - 40px);
  background: #21004d;
  border: 2px solid rgba(255,255,255,0.2);
  border-radius: 20px;
  overflow: hidden;
  position: relative;
  margin: 0;

  /* 완전한 중앙 정렬 */
  box-sizing: border-box;

  /* 그림자와 시각적 효과 */
  box-shadow:
    0 25px 50px -12px rgba(0, 0, 0, 0.8),
    0 0 0 1px rgba(255, 255, 255, 0.05),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);

  /* 애니메이션 */
  animation: modalSlideIn 0.4s cubic-bezier(0.16, 1, 0.3, 1);

  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: scale(0.9) translateY(-20px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }

  /* 스크롤 영역 */
  overflow-y: auto;

  /* 스크롤바 스타일링 */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 4px;

    &:hover {
      background: rgba(255, 255, 255, 0.5);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 28px;
  border-bottom: 1px solid rgba(255,255,255,0.15);
  background: linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%);
`;

const ModalBody = styled.div`
  padding: 28px;
  background: linear-gradient(180deg, rgba(255,255,255,0.01) 0%, rgba(255,255,255,0.03) 100%);
`;

const CloseButton = styled.button`
  border: none;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 20px;
  cursor: pointer;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  border: none;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.9);
  color: #333;
  font-size: 16px;
  transition: all 0.2s ease;

  &::placeholder {
    color: rgba(0,0,0,0.5);
  }

  &:focus { outline: none; background: #fff; transform: translateY(-1px); }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 14px 16px;
  border: none;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.9);
  color: #333;
  font-size: 16px;
  transition: all 0.2s ease;
  resize: vertical;

  &::placeholder { color: rgba(0,0,0,0.5); }
  &:focus { outline: none; background: #fff; transform: translateY(-1px); }
`;

const Row = styled.div`
  display: grid;
  gap: 16px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
`;

const Primary = styled.button`
  padding: 14px 22px;
  border-radius: 8px;
  background: rgb(245,124,10);
  border: 3px solid rgb(235,98,0);
  color: #000;
  cursor: pointer;
`;

const Secondary = styled.button`
  padding: 14px 22px;
  border-radius: 8px;
  background: transparent;
  border: 2px solid #fff;
  color: #fff;
  cursor: pointer;
`;

export interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialSource?: string;
}

interface Step1FormData {
  name: string;
  email: string;
  company: string;
  phone: string;
  title: string;
  message: string;
}

const defaultStep1: Step1FormData = {
  name: '',
  email: '',
  company: '',
  phone: '',
  title: '',
  message: '',
};

export const ContactModal: React.FC<ContactModalProps> = ({ isOpen, onClose, initialSource }) => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [data, setData] = useState<Step1FormData>(defaultStep1);

  const source = useMemo(() => initialSource || localStorage.getItem('cta_source') || 'unknown', [initialSource]);

  useEffect(() => {
    if (initialSource) {
      localStorage.setItem('cta_source', initialSource);
    }
  }, [initialSource]);

  useEffect(() => {
    if (!isOpen) {
      setData(defaultStep1);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.currentTarget === e.target) onClose();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setData(prev => ({ ...prev, [name]: value }));
  };

  const canProceedStep1 = (
    data.name.trim() &&
    data.email.trim() &&
    data.company.trim() &&
    data.phone.trim() &&
    data.title.trim() &&
    data.message.trim()
  );

  const submit = async () => {
    setIsSubmitting(true);
    try {
      const payload = { source, ...data };
      console.log('[CONTACT SUBMIT]', payload);
      
      await fetch(N8N_WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      localStorage.setItem('last_contact_payload', JSON.stringify(payload));
      onClose();
    } catch (e) {
      console.error(e);
      alert('전송에 실패했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Overlay onClick={handleBackdropClick} role="dialog" aria-modal="true">
      <Modal>
        <ModalHeader>
          <div>
            <h2 style={{ fontFamily: 'HGAlmond 80g, sans-serif', fontWeight: 'normal' }}>도입 문의</h2>
          </div>
          <CloseButton onClick={onClose} aria-label="닫기">✕</CloseButton>
        </ModalHeader>
        <ModalBody>
          <Container>
            <div style={{ maxWidth: 680, margin: '0 auto' }}>
              <Row>
                <div>
                  <Label htmlFor="name">이름</Label>
                  <Input id="name" name="name" value={data.name} onChange={handleChange} placeholder="홍길동" required />
                </div>
                <div>
                  <Label htmlFor="email">메일 주소</Label>
                  <Input id="email" type="email" name="email" value={data.email} onChange={handleChange} placeholder="you@example.com" required />
                </div>
                <div>
                  <Label htmlFor="company">회사명</Label>
                  <Input id="company" name="company" value={data.company} onChange={handleChange} placeholder="(주)쓸모" required />
                </div>
                <div>
                  <Label htmlFor="phone">연락처</Label>
                  <Input id="phone" name="phone" value={data.phone} onChange={handleChange} placeholder="010-0000-0000" required />
                </div>
                <div>
                  <Label htmlFor="title">직책</Label>
                  <Input id="title" name="title" value={data.title} onChange={handleChange} placeholder="매니저" required />
                </div>
                <div>
                  <Label htmlFor="message">문의 내용</Label>
                  <Textarea id="message" name="message" value={data.message} onChange={handleChange} rows={6} placeholder="도입 배경/요청 사항 등을 적어주세요." required />
                </div>
                <Actions>
                  <Primary onClick={submit} disabled={!canProceedStep1 || isSubmitting}>
                    {isSubmitting ? '전송 중...' : '제출하기'}
                  </Primary>
                </Actions>
              </Row>
            </div>
          </Container>
        </ModalBody>
      </Modal>
    </Overlay>
  );
};

export default ContactModal;
